import React, { useMemo, useState } from 'react';
import { pdf } from '@react-pdf/renderer';
import { Download, Eye, Loader2 } from 'lucide-react';
import type { UseFormRegister, UseFormWatch } from 'react-hook-form';
import { SalesOrderInvoicePDF } from '../../components/sales-orders/SalesOrderInvoicePDF';
import { Button } from '../../components/common/Button';
import { ConfirmationDialog } from '../../components/common/ConfirmationDialog';
import { useCompanyLogo } from '../../hooks/useCompanyLogo';
import type { SalesOrderFormData, SalesOrder } from '../../types/salesOrder';
import type { Customer } from '../../types/customer';
import type { Company } from '../../store/authStore';
import type { User } from '../../store/authStore';
import { useLanguage } from '../../features/language/useLanguage';

interface Props {
  register: UseFormRegister<SalesOrderFormData>;
  watch: UseFormWatch<SalesOrderFormData>;
  initialData?: SalesOrderFormData;
  totalAmount: number;
  currencySymbol: string;
  company: Company | null;
  user: User | null;
  customers: Customer[];
  loading: boolean;
  isSubmitting: boolean;
  isSuccess: boolean;
  isError: boolean;
  onCancel: () => void;
  isCancelWarningOpen: boolean;
  setIsCancelWarningOpen: (value: boolean) => void;
  pendingSubmitData: SalesOrderFormData | null;
  processSubmit: (data: SalesOrderFormData) => Promise<void>;
  isSubmitDisabled: boolean;
  readOnly?: boolean;
}

export const SalesOrderFooterSection: React.FC<Props> = ({
  register,
  watch,
  initialData,
  totalAmount,
  currencySymbol,
  company,
  user,
  customers,
  loading,
  isSubmitting,
  isSuccess,
  isError,
  onCancel,
  isCancelWarningOpen,
  setIsCancelWarningOpen,
  pendingSubmitData,
  processSubmit,
  isSubmitDisabled,
  readOnly = false,
}) => {
  const { t } = useLanguage();
  const watchedCustomer = watch('customer');
  const customer = useMemo(
    () => customers.find(c => c.name === watchedCustomer) || (watchedCustomer ? { name: watchedCustomer } : null),
    [customers, watchedCustomer]
  );
  const { data: logoDataUrl } = useCompanyLogo();
  const [isPreviewingInvoice, setIsPreviewingInvoice] = useState(false);
  const [isDownloadingInvoice, setIsDownloadingInvoice] = useState(false);

  const buildInvoiceDocument = React.useCallback(() => {
    if (!initialData || !('id' in initialData)) return null;

    return (
      <SalesOrderInvoicePDF
        order={{
          ...initialData,
          ...watch(),
          id: (initialData as SalesOrder).id || '',
          orderNumber: (initialData as SalesOrder).orderNumber || 'DRAFT',
          totalAmount,
          createdAt: (initialData as SalesOrder).createdAt || new Date().toISOString(),
          updatedAt: (initialData as SalesOrder).updatedAt || new Date().toISOString(),
        } as SalesOrder}
        companyInfo={company ? {
          ...company,
          email: company.email || user?.email,
          phone: company.phone,
          logoDataUrl: logoDataUrl || null,
        } : null}
        customer={customer}
        currencySymbol={currencySymbol}
      />
    );
  }, [company, currencySymbol, customer, initialData, logoDataUrl, totalAmount, user?.email, watch]);

  const getInvoiceFileName = React.useCallback(
    () => `Invoice-${(initialData as SalesOrder | undefined)?.orderNumber || 'DRAFT'}.pdf`,
    [initialData]
  );

  const handlePreviewInvoice = async () => {
    const invoiceDocument = buildInvoiceDocument();
    if (!invoiceDocument || isPreviewingInvoice) return;

    // Open the tab synchronously inside the click gesture so popup blockers
    // don't cancel it, then point it at the generated blob once ready.
    const win = window.open('', '_blank');
    setIsPreviewingInvoice(true);
    try {
      const blob = await pdf(invoiceDocument).toBlob();
      const url = URL.createObjectURL(blob);
      if (win) {
        win.location.href = url;
      } else {
        // Popup was blocked — fall back to a same-tab navigation.
        window.open(url, '_blank');
      }
      // Release the object URL after the tab has had time to load it.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      console.error('Failed to generate invoice PDF', error);
      if (win) win.close();
    } finally {
      setIsPreviewingInvoice(false);
    }
  };

  const handleDownloadInvoice = async () => {
    const invoiceDocument = buildInvoiceDocument();
    if (!invoiceDocument || isDownloadingInvoice) return;

    setIsDownloadingInvoice(true);
    try {
      const blob = await pdf(invoiceDocument).toBlob();
      const url = URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = getInvoiceFileName();
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 5_000);
    } catch (error) {
      console.error('Failed to download invoice PDF', error);
    } finally {
      setIsDownloadingInvoice(false);
    }
  };

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700">{t('salesOrder.footer.messageOnInvoice')}</label>
          <textarea
            data-test-id="so-message-on-invoice"
            {...register('messageOnInvoice')}
            rows={4}
            readOnly={readOnly}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">{t('forms.common.internalNotes')}</label>
          <textarea
            {...register('internalNotes')}
            rows={4}
            readOnly={readOnly}
            className="mt-1 p-2.5 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border"
            placeholder={t('salesOrder.footer.internalNotesPlaceholder')}
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        {initialData && 'id' in initialData && (
          <div className="inline-flex rounded-md shadow-sm">
            <button
              type="button"
              data-test-id="so-preview-invoice"
              onClick={handlePreviewInvoice}
              disabled={isPreviewingInvoice || isDownloadingInvoice}
              title={t('salesOrder.footer.previewInvoice')}
              aria-label={t('salesOrder.footer.previewInvoice')}
              className="inline-flex items-center rounded-l-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              {isPreviewingInvoice ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
            </button>
            <button
              type="button"
              data-test-id="so-download-invoice"
              onClick={handleDownloadInvoice}
              disabled={isPreviewingInvoice || isDownloadingInvoice}
              title={t('salesOrder.footer.downloadInvoice')}
              aria-label={t('salesOrder.footer.downloadInvoice')}
              className="-ml-px inline-flex items-center rounded-r-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              <Download className="mr-2 h-4 w-4" />
              {isDownloadingInvoice ? t('salesOrder.footer.preparing') : t('salesOrder.footer.downloadInvoice')}
            </button>
          </div>
        )}
        <button
          data-test-id="so-create-cancel"
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          {t('auth.common.cancel')}
        </button>
        {!readOnly && (
          <Button
            data-test-id="so-create-submit"
            type="submit"
            isLoading={loading || isSubmitting}
            isSuccess={isSuccess}
            isError={isError}
            disabled={isSubmitDisabled}
          >
            {initialData ? t('salesOrder.footer.updateOrder') : t('salesOrder.footer.createOrder')}
          </Button>
        )}
      </div>
      
      <ConfirmationDialog
        isOpen={isCancelWarningOpen}
        onClose={() => setIsCancelWarningOpen(false)}
        onConfirm={() => {
          if (pendingSubmitData) {
            processSubmit(pendingSubmitData);
          }
          setIsCancelWarningOpen(false);
        }}
        title={t('salesOrder.footer.confirmCancellationTitle')}
        message={t('salesOrder.footer.confirmCancellationMessage')}
        confirmLabel={t('salesOrder.footer.confirmCancelOrder')}
        isDestructive={true}
      />
    </>
  );
};
